import { apiRequest, apiEnabled } from './api';
import { activities as staticActivities, destinations as staticDestinations } from '../siteData';

// Admin data layer for activities. Reads fall back to the bundled static
// content when there is no backend configured (so the panel still renders a
// read-only preview); writes require the backend + an admin session.

export { apiEnabled };

// `active`: true = only visible, false = only hidden, undefined = all.
// Uses the admin endpoint so deactivated activities remain listed (the public
// /activities endpoint filters them out and they'd be impossible to re-enable).
export async function fetchAdminActivities(active) {
  if (!apiEnabled) {
    return { items: staticActivities, total: staticActivities.length, source: 'static' };
  }
  const query = { limit: 200 };
  if (active !== undefined) query.active = active;
  const res = await apiRequest('/api/admin/activities', { auth: true, query });
  return {
    items: res.items || [],
    total: res.total || 0,
    activeCount: res.active_count ?? 0,
    inactiveCount: res.inactive_count ?? 0,
    source: 'api',
  };
}

export function fetchAdminDestinations() {
  return staticDestinations.map((d) => ({ slug: d.id, name: d.name }));
}

export const LANGS = [
  { id: 'es', label: 'ES', name: 'Español' },
  { id: 'en', label: 'EN', name: 'Inglés' },
  { id: 'it', label: 'IT', name: 'Italiano' },
  { id: 'de', label: 'DE', name: 'Alemán' },
  { id: 'no', label: 'NO', name: 'Noruego' },
  { id: 'nl', label: 'NL', name: 'Neerlandés' },
];

// Serialise an i18n field. Only keys present in the form are sent: untouched
// languages are left out so the backend merge keeps whatever is stored, while a
// language the user deliberately cleared is sent as "" and does get cleared.
function cleanI18n(obj, { required = false } = {}) {
  const out = {};
  Object.entries(obj || {}).forEach(([lang, value]) => {
    out[lang] = typeof value === 'string' ? value.trim() : value;
  });
  if (required && !out.es) out.es = '';
  return out;
}

// Build the ActivityUpsert body the backend expects. Title/excerpt/description
// are i18n dicts covering all six languages the site ships.
export function toActivityPayload(form, { isNew = false } = {}) {
  const payload = {
    slug: form.slug.trim(),
    title: cleanI18n(form.title, { required: true }),
    excerpt: cleanI18n(form.excerpt),
    description: cleanI18n(form.description),
    island: form.island,
    location: form.location || '',
    category: form.category,
    duration: form.duration || '',
    price: Number(form.price) || 0,
    original_price: form.original_price ? Number(form.original_price) : null,
    price_unit: form.price_unit || 'persona',
    currency: 'EUR',
    featured: Boolean(form.featured),
    booking_enabled: Boolean(form.booking_enabled),
    active: true,
    noindex: Boolean(form.noindex),
  };
  payload.seo_keyphrase = (form.seo_keyphrase || '').trim();
  payload.canonical_url = (form.canonical_url || '').trim();
  if (form.seo_title && typeof form.seo_title === 'object') {
    const st = cleanI18n(form.seo_title);
    if (Object.values(st).some((v) => v)) payload.seo_title = st;
  }
  if (form.seo_description && typeof form.seo_description === 'object') {
    const sd = cleanI18n(form.seo_description);
    if (Object.values(sd).some((v) => v)) payload.seo_description = sd;
  }
  // Bullet lists are arrays of i18n objects. Rows whose every language is empty
  // are dropped. Only sent when the form actually loaded them, so a partial edit
  // never blanks the stored lists.
  const cleanList = (list) =>
    (list || [])
      .map((item) => cleanI18n(item))
      .filter((item) => Object.values(item).some((v) => v));

  if (Array.isArray(form.included)) payload.included = cleanList(form.included);
  if (Array.isArray(form.not_included)) payload.not_included = cleanList(form.not_included);

  // Booking config. ticket_types drive the real charged price server-side, so
  // only send them when the form actually loaded them.
  if (Array.isArray(form.ticket_types)) {
    payload.ticket_types = form.ticket_types
      .filter((t) => (t.id || '').trim() && Number(t.price) > 0)
      .map((t) => ({
        id: (t.id || '').trim(),
        label: (() => {
          const obj = typeof t.label === 'object' && t.label ? cleanI18n(t.label) : { es: (t.label || '').trim() };
          if (!obj.es) obj.es = (t.id || '').trim();
          return obj;
        })(),
        price: Number(t.price),
        // Deposit charged online per unit (rest paid on site). 0 = charge full.
        deposit: t.deposit === '' || t.deposit == null ? 0 : Number(t.deposit) || 0,
        min: Number(t.min) || 0,
        max: Number(t.max) || 12,
        default: Number(t.default) || 0,
        seats: Number(t.seats) || 1,
      }));
  }
  if (form.capacity !== '' && form.capacity != null) {
    const cap = Number(form.capacity);
    if (cap > 0) payload.capacity = cap;
  }
  if (Array.isArray(form.time_slots)) {
    payload.time_slots = form.time_slots.map((s) => (s || '').trim()).filter(Boolean);
  }
  if (form.cancellation_policy && typeof form.cancellation_policy === 'object') {
    const cp = form.cancellation_policy;
    // short/details are i18n dicts (with legacy plain-string tolerance).
    const i18nField = (v) => {
      if (v && typeof v === 'object') return cleanI18n(v);
      const s = (v || '').trim();
      return s ? { es: s } : {};
    };
    const short = i18nField(cp.short);
    const details = i18nField(cp.details);
    const policy = { ...cp, noticeHours: Number(cp.noticeHours) || 0, short, details };
    const hasText = Object.values(short).some((x) => x) || Object.values(details).some((x) => x);
    if (hasText || policy.noticeHours) payload.cancellation_policy = policy;
  }

  if (form.meeting_point && typeof form.meeting_point === 'object') {
    const mp = cleanI18n(form.meeting_point);
    if (Object.values(mp).some((v) => v)) payload.meeting_point = mp;
  }

  // The gallery is edited explicitly in the panel. Only send image/gallery when
  // we actually have values, so a partial edit never blanks them server-side
  // (the backend merges with exclude_unset).
  const image = (form.image || '').trim();
  if (image) payload.image = image;
  payload.video_url = (form.video_url || '').trim();

  if (Array.isArray(form.gallery)) {
    const gallery = form.gallery.map((url) => (url || '').trim()).filter(Boolean);
    if (gallery.length) payload.gallery = gallery;
    else if (isNew && image) payload.gallery = [image];
  } else if (isNew && image) {
    payload.gallery = [image];
  }
  return payload;
}

export function createActivity(payload) {
  return apiRequest('/api/admin/activities', {
    method: 'POST',
    auth: true,
    body: JSON.stringify(payload),
  });
}

export function updateActivity(slug, payload) {
  return apiRequest(`/api/admin/activities/${slug}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(payload),
  });
}

export function saveActivityAvailability(slug, { capacity_per_slot, time_slots, blackout_dates, ical_urls }) {
  return apiRequest(`/api/admin/activities/${slug}/availability`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify({ capacity_per_slot, time_slots, blackout_dates, ical_urls }),
  });
}

export function saveActivityAddons(slug, addons) {
  return apiRequest(`/api/admin/activities/${slug}/addons`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify({ addons }),
  });
}

export function syncActivityCalendar(slug) {
  return apiRequest(`/api/admin/activities/${slug}/sync-calendar`, { method: 'POST', auth: true });
}

export function fetchCalendarFeed() {
  return apiRequest('/api/admin/settings/calendar', { auth: true });
}

export function deleteActivity(slug) {
  return apiRequest(`/api/admin/activities/${slug}`, {
    method: 'DELETE',
    auth: true,
  });
}
